/* eslint-disable react/jsx-sort-props */
/* eslint-disable react/jsx-max-props-per-line */
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import { Box, Stack, Typography, Button } from '@mui/material';
import {
  tableV2MultilineGridSx,
  tableV2StackedCellContentSx,
} from '../../../components/v2/tableV2Styles';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import { TableV2, CustomFilterV2 } from '../../../components/v2';
import { renderStatus } from 'components/v2/cell-renderers/status';
import * as actions from '../../../store/procedimentosNormas/actions';
import * as selectorsAuth from '../../../store/auth/reducer';
import * as selectorsModuleConfiguration from '../../../store/moduleConfiguration/reducer';
import { PROCEDIMENTO_NORMAS_FULL_ACCESS } from 'constants/modules';
import * as Roles from '../../../constants/role';
import Formulario from './Formulario';
import ResumoCards from './ResumoCards';

const statusOptions = [
  { value: 'ACCEPTED', label: 'Confirmado' },
  { value: 'AVAILABLE', label: 'Aguardando Confirmação' },
  { value: 'CANCEL', label: 'Cancelado' },
];

const statusLabel = {
  AVAILABLE: 'Aguardando Confirmação',
  ACCEPTED: 'Confirmado',
  CANCEL: 'Cancelado',
};

const ProcedimentoNormaV2 = ({ authData, deleteProcedimentoNorma, moduleConfigurations }) => {
  const fullAccess = useMemo(
    () => moduleConfigurations?.some(u => u.codigo === PROCEDIMENTO_NORMAS_FULL_ACCESS && u.userId === authData.id),
    [moduleConfigurations, authData]
  );

  const defaultFilter = useMemo(() => ({
    inicio: moment().subtract(1, 'month').startOf('month').valueOf(),
    fim: moment().endOf('month').valueOf(),
    usuarioId: !fullAccess ? [authData.id] : [],
    modulos: [],
    tipoServicos: [],
    status: [],
  }), [fullAccess, authData]);

  const [open, setOpen] = useState(false);
  const [id, setId] = useState(null);
  const [localFilter, setLocalFilter] = useState(defaultFilter);
  const [activeStatus, setActiveStatus] = useState(null);
  const [reload, setReload] = useState(false);
  const [openFilter, setOpenFilter] = useState(false);

  const apiFilterBase = useMemo(() => {
    const rest = { ...localFilter };
    delete rest.status;
    return rest;
  }, [localFilter]);

  const apiFilter = useMemo(
    () => ({
      ...localFilter,
      status: activeStatus ? [activeStatus] : localFilter.status,
    }),
    [localFilter, activeStatus]
  );

  const onClose = (save) => {
    setOpen(false);
    setId(null);
    if (save) setReload(r => !r);
  };

  const onDelete = (rowId) => {
    deleteProcedimentoNorma(rowId, () => setReload(r => !r));
  };

  const isAdmin = authData.role === Roles.ADMIN || authData.role === Roles.SUPER_ADMIN;

  const headerActions = fullAccess
    ? [{ label: 'Novo', onClick: () => { setOpen(true); setId(null); }, startIcon: <AddIcon />, variant: 'contained' }]
    : [];

  const columns = [
    {
      field: 'title',
      headerName: 'Título',
      flex: 1,
      minWidth: 200,
      renderCell: ({ row }) => (
        <Box sx={tableV2StackedCellContentSx}>
          <Typography variant="body2" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
            {row.title}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Criado em {row.dataInclusaoValue}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'moduloNome',
      headerName: 'Módulo',
      flex: 0.8,
      minWidth: 180,
      renderCell: ({ row }) => (
        <Box sx={tableV2StackedCellContentSx}>
          <Typography variant="body2">{row.moduloNome}</Typography>
          {row.tipoServicoNome && (
            <Typography variant="caption" color="text.secondary">
              {row.tipoServicoNome}
              {row.subtipoNome ? ` / ${row.subtipoNome}` : ''}
            </Typography>
          )}
        </Box>
      ),
    },
    { field: 'usuarioNome', headerName: 'Usuário', flex: 0.6, minWidth: 140 },
    {
      field: 'statusValue',
      headerName: 'Status',
      width: 220,
      renderCell: ({ row }) => (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'flex-start',
            height: '100%',
            gap: 0.25,
          }}
        >
          <Box sx={{ width: 'fit-content' }}>
            {renderStatus({ value: row.status, label: statusLabel[row.status] })}
          </Box>
          {row.status === 'CANCEL' && row.dataCanceladaValue && (
            <Typography variant="caption" color="text.secondary">
              Cancelado em {row.dataCanceladaValue}
            </Typography>
          )}
          {row.status === 'ACCEPTED' && row.dataConfirmacaoValue && (
            <Typography variant="caption" color="text.secondary">
              Confirmado em {row.dataConfirmacaoValue}
            </Typography>
          )}
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Stack spacing={3}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
        >
          <Typography variant="h4">Procedimentos e Normas</Typography>
          <Button
            variant="outlined"
            startIcon={<FilterListOutlinedIcon />}
            onClick={() => setOpenFilter(true)}
          >
            Filtros
          </Button>
        </Stack>

        <ResumoCards
          apiFilterBase={apiFilterBase}
          activeStatus={activeStatus}
          onFilterByStatus={setActiveStatus}
          refreshKey={reload}
        />

        <TableV2
          useApi
          fetchUrl="procedimentos-normas/filter"
          columns={columns}
          getRowHeight={() => 'auto'}
          gridSx={tableV2MultilineGridSx}
          headerActions={headerActions}
          pageSize={10}
          actions={[
            {
              key: 'edit',
              icon: <EditIcon fontSize="small" />,
              title: 'Editar',
              show: row => fullAccess || row.isUsuario,
              onClick: (id) => { setOpen(true); setId(id); },
            },
          ]}
          onDelete={onDelete}
          canDelete={row => fullAccess || row.fullAccess}
          filterData={apiFilter}
          refreshKey={reload}
        />

        <CustomFilterV2
          variant="dialog"
          open={openFilter}
          onClose={() => setOpenFilter(false)}
          title="Filtros"
          fullWidth
          fields={[
            { field: 'useDates' },
            {
              field: 'useUsuarios',
              md: 6,
              multiSelect: true,
              disabled: !isAdmin,
            },
            {
              field: 'useOption',
              md: 6,
              code: 'PROCEDIMENTOS_NORMAS_MODULOS',
              label: 'Módulos',
              key: 'modulos',
            },
            { field: 'useTipoServico', md: 6 },
            {
              field: 'useMultiSelectCustomOption',
              label: 'Status',
              key: 'status',
              md: 6,
              options: statusOptions,
            },
          ]}
          defaultFilter={defaultFilter}
          appliedFilter={apiFilter}
          onFilter={filterData => {
            setActiveStatus(null);
            setLocalFilter(filterData);
            setOpenFilter(false);
          }}
        />

        <Formulario
          id={id}
          open={open}
          onClose={onClose}
          onReload={() => setReload(r => !r)}
        />
      </Stack>
    </Box>
  );
};

ProcedimentoNormaV2.propTypes = {
  authData: PropTypes.object,
  deleteProcedimentoNorma: PropTypes.func.isRequired,
  moduleConfigurations: PropTypes.array,
};

const mapStateToProps = state => ({
  authData: selectorsAuth.getAuth(state),
  moduleConfigurations: selectorsModuleConfiguration.getModuleConfigurations(state),
});

const mapDispatchToProps = dispatch => bindActionCreators({
  ...actions,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(ProcedimentoNormaV2));
